import { useState, useEffect } from 'react';
import { Shield, Search, Download, Settings, ChevronRight, Clock, User, Briefcase, FileText, Mail } from 'lucide-react';
import api from '../../lib/api';
import { Card, CardContent } from '../../components/ui/Card';
import Badge from '../../components/ui/Badge';
import Input from '../../components/ui/Input';
import Button from '../../components/ui/Button';

interface AuditLog {
  id: string;
  action: string;
  entityType: string;
  entityId: string;
  actor?: {
    id: string;
    email: string;
    fullName?: string;
  };
  actorId?: string;
  before?: any;
  after?: any;
  createdAt: string;
}

export default function AuditLogs() {
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [selectedLog, setSelectedLog] = useState<AuditLog | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [entityTypeFilter, setEntityTypeFilter] = useState<string>('ALL');
  const [actionFilter, setActionFilter] = useState<string>('ALL');
  const [userFilter, setUserFilter] = useState<string>('ALL');
  const [developerMode, setDeveloperMode] = useState(false);
  const [dateFilter, setDateFilter] = useState<string>('7'); // Last 7 days

  useEffect(() => {
    loadLogs();
  }, [entityTypeFilter, actionFilter, dateFilter]);

  const loadLogs = async () => {
    try {
      setLoading(true);
      const params: any = { limit: 100 };
      if (entityTypeFilter !== 'ALL') params.entityType = entityTypeFilter;
      if (actionFilter !== 'ALL') params.action = actionFilter;

      const response = await api.get('/audit', { params });
      setLogs(response.data);
      if (response.data.length > 0 && !selectedLog) {
        setSelectedLog(response.data[0]);
      }
    } catch (error) {
      console.error('Failed to load audit logs:', error);
    } finally {
      setLoading(false);
    }
  };

  // Generate human-readable summary
  const generateSummary = (log: AuditLog): string => {
    const actor = log.actor?.fullName || log.actor?.email || 'System';
    const entity = log.entityType.replace(/([A-Z])/g, ' $1').trim();
    
    // Extract contextual data from 'after' payload
    const jobTitle = log.after?.job?.title || log.after?.title || '';
    const kolName = log.after?.kolProfile?.name || log.after?.name || '';
    
    switch (log.action) {
      case 'CREATE':
        if (jobTitle) return `${actor} created ${entity} "${jobTitle}"`;
        if (kolName) return `${actor} created ${entity} for ${kolName}`;
        return `${actor} created a new ${entity}`;
      
      case 'UPDATE':
      case 'UPDATE_STATUS':
        if (jobTitle) return `${actor} updated ${entity} "${jobTitle}"`;
        if (kolName) return `${actor} updated ${entity} for ${kolName}`;
        return `${actor} updated ${entity}`;
      
      case 'DELETE':
      case 'REMOVE_MEMBER':
        return `${actor} removed ${entity}`;
      
      case 'BROADCAST':
        return `${actor} broadcasted job "${jobTitle}" to KOLs`;
      
      case 'RESPOND': {
        const status = log.after?.status;
        return `${actor} ${status?.toLowerCase()} an invitation`;
      }
      
      case 'ADD_MEMBER':
        return `${actor} added a member to ${entity}`;
      
      case 'APPROVE':
        return `${actor} approved ${entity}`;
      
      case 'REJECT':
        return `${actor} rejected ${entity}`;
      
      case 'SEND_MESSAGE':
        return `${actor} sent a message`;
      
      default:
        return `${actor} performed ${log.action} on ${entity}`;
    }
  };

  // Extract contextual data
  const getContextualData = (log: AuditLog) => {
    return {
      jobTitle: log.after?.job?.title || log.after?.title,
      kolName: log.after?.kolProfile?.name || log.after?.name,
      status: log.after?.status,
      email: log.after?.email,
      targetUser: log.after?.user?.fullName || log.after?.user?.email,
    };
  };

  // Filter logs
  const filteredLogs = logs.filter((log) => {
    if (userFilter !== 'ALL' && log.actor?.email !== userFilter) return false;
    
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    const summary = generateSummary(log).toLowerCase();
    return (
      summary.includes(search) ||
      log.action.toLowerCase().includes(search) ||
      log.entityType.toLowerCase().includes(search) ||
      log.entityId.toLowerCase().includes(search)
    );
  });

  // Group logs by date
  const groupedLogs = filteredLogs.reduce((groups, log) => {
    const date = new Date(log.createdAt).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
    if (!groups[date]) groups[date] = [];
    groups[date].push(log);
    return groups;
  }, {} as Record<string, AuditLog[]>);

  // Get unique users for filter
  const uniqueUsers = Array.from(new Set(logs.map(l => l.actor?.email).filter(Boolean)));

  const entityTypes = ['ALL', 'Job', 'KOLProfile', 'Invitation', 'ScriptDraft', 'Submission', 'Organization', 'User'];
  const actions = ['ALL', 'CREATE', 'UPDATE', 'DELETE', 'BROADCAST', 'RESPOND', 'APPROVE', 'REJECT'];

  const getActionBadge = (action: string) => {
    const config: Record<string, { variant: any; icon: string }> = {
      CREATE: { variant: 'success', icon: '🟢' },
      UPDATE: { variant: 'default', icon: '🟡' },
      UPDATE_STATUS: { variant: 'default', icon: '🟡' },
      DELETE: { variant: 'destructive', icon: '🔴' },
      BROADCAST: { variant: 'default', icon: '🔵' },
      RESPOND: { variant: 'default', icon: '✉️' },
      APPROVE: { variant: 'success', icon: '✅' },
      REJECT: { variant: 'destructive', icon: '❌' },
    };
    const { variant, icon } = config[action] || { variant: 'secondary', icon: '⚙️' };
    return <Badge variant={variant}>{icon} {action}</Badge>;
  };

  const getEntityIcon = (entityType: string) => {
    switch (entityType) {
      case 'Job':
        return <Briefcase className="w-4 h-4" />;
      case 'ScriptDraft':
      case 'Submission':
        return <FileText className="w-4 h-4" />;
      case 'Invitation':
      case 'InvitationMessage':
        return <Mail className="w-4 h-4" />;
      case 'User':
      case 'KOLProfile':
        return <User className="w-4 h-4" />;
      default:
        return <Shield className="w-4 h-4" />;
    }
  };

  const exportLogs = () => {
    const csvContent = [
      ['Timestamp', 'Actor', 'Action', 'Entity Type', 'Summary'].join(','),
      ...filteredLogs.map(log =>
        [
          new Date(log.createdAt).toISOString(),
          log.actor?.email || log.actorId || 'System',
          log.action,
          log.entityType,
          generateSummary(log),
        ]
          .map(v => `"${v}"`)
          .join(',')
      ),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `audit-logs-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const contextData = selectedLog ? getContextualData(selectedLog) : null;

  return (
    <div className="h-full flex flex-col">
      {/* Header with Filters */}
      <div className="flex-shrink-0 space-y-4 mb-6">
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-[2.25rem] font-semibold tracking-tightest text-glass-text flex items-center gap-2">
              <Shield className="w-8 h-8 text-accent2" />
              Audit Logs
            </h1>
            <p className="text-glass-text-2 mt-1">Complete activity tracking for compliance</p>
          </div>
          <div className="flex gap-2">
            <Button
              variant={developerMode ? 'default' : 'secondary'}
              onClick={() => setDeveloperMode(!developerMode)}
              className="flex items-center gap-2"
            >
              <Settings className="w-4 h-4" />
              Developer Mode
            </Button>
            <Button onClick={exportLogs} className="flex items-center gap-2">
              <Download className="w-4 h-4" />
              Export CSV
            </Button>
          </div>
        </div>

        {/* Filter Toolbar */}
        <Card>
          <CardContent className="p-4">
            <div className="grid grid-cols-12 gap-3">
              {/* Search */}
              <div className="col-span-12 md:col-span-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-glass-text-3 w-4 h-4" />
                  <Input
                    type="text"
                    placeholder="Search actions, users, entities..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-9 text-sm"
                  />
                </div>
              </div>

              {/* User Filter */}
              <div className="col-span-6 md:col-span-2">
                <select
                  value={userFilter}
                  onChange={(e) => setUserFilter(e.target.value)}
                  className="w-full px-3 py-2 text-sm rounded-lg border border-glass bg-glass-2 text-glass-text focus:outline-none focus:ring-2 focus:ring-accent2/40"
                >
                  <option value="ALL">All Users</option>
                  {uniqueUsers.map((email) => (
                    <option key={email} value={email}>{email}</option>
                  ))}
                </select>
              </div>

              {/* Entity Type Filter */}
              <div className="col-span-6 md:col-span-2">
                <select
                  value={entityTypeFilter}
                  onChange={(e) => setEntityTypeFilter(e.target.value)}
                  className="w-full px-3 py-2 text-sm rounded-lg border border-glass bg-glass-2 text-glass-text focus:outline-none focus:ring-2 focus:ring-accent2/40"
                >
                  {entityTypes.map((type) => (
                    <option key={type} value={type}>
                      {type === 'ALL' ? 'All Types' : type}
                    </option>
                  ))}
                </select>
              </div>

              {/* Action Filter */}
              <div className="col-span-6 md:col-span-2">
                <select
                  value={actionFilter}
                  onChange={(e) => setActionFilter(e.target.value)}
                  className="w-full px-3 py-2 text-sm rounded-lg border border-glass bg-glass-2 text-glass-text focus:outline-none focus:ring-2 focus:ring-accent2/40"
                >
                  {actions.map((action) => (
                    <option key={action} value={action}>
                      {action === 'ALL' ? 'All Actions' : action}
                    </option>
                  ))}
                </select>
              </div>

              {/* Date Range Filter */}
              <div className="col-span-6 md:col-span-2">
                <select
                  value={dateFilter}
                  onChange={(e) => setDateFilter(e.target.value)}
                  className="w-full px-3 py-2 text-sm rounded-lg border border-glass bg-glass-2 text-glass-text focus:outline-none focus:ring-2 focus:ring-accent2/40"
                >
                  <option value="1">Last 24 hours</option>
                  <option value="7">Last 7 days</option>
                  <option value="30">Last 30 days</option>
                  <option value="90">Last 90 days</option>
                  <option value="365">Last year</option>
                </select>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Two-Column Layout */}
      <div className="flex-1 grid grid-cols-12 gap-6 min-h-0">
        {/* Left Panel - Activity List */}
        <div className="col-span-12 lg:col-span-5 flex flex-col min-h-0">
          <Card className="flex-1 flex flex-col min-h-0">
            <div className="p-4 border-b border-glass flex-shrink-0">
              <h2 className="font-semibold text-glass-text">
                Activity Log ({filteredLogs.length})
              </h2>
            </div>
            <CardContent className="flex-1 overflow-y-auto p-0">
              {loading ? (
                <div className="text-center py-12">
                  <p className="text-glass-text-3">Loading...</p>
                </div>
              ) : Object.keys(groupedLogs).length === 0 ? (
                <div className="text-center py-12 px-4">
                  <Shield className="w-12 h-12 text-glass-text-3 mx-auto mb-4" />
                  <p className="text-glass-text-3">No audit logs found</p>
                </div>
              ) : (
                Object.entries(groupedLogs).map(([date, dateLogs]) => (
                  <div key={date} className="border-b border-glass last:border-b-0">
                    <div className="px-4 py-2 bg-glass-3 sticky top-0 z-10 border-b border-glass">
                      <p className="text-xs font-semibold text-glass-text-2 uppercase tracking-[0.05em]">
                        {date}
                      </p>
                    </div>
                    <div>
                      {dateLogs.map((log) => (
                        <div
                          key={log.id}
                          onClick={() => setSelectedLog(log)}
                          className={`px-4 py-3 cursor-pointer border-l-4 transition-all ${
                            selectedLog?.id === log.id
                              ? 'bg-accent2/10 border-l-accent2'
                              : 'border-l-transparent hover:bg-glass-2'
                          }`}
                        >
                          <div className="flex items-start gap-3">
                            <div className="flex-shrink-0 p-2 rounded-lg border border-glass bg-glass-2 text-glass-text-2">
                              {getEntityIcon(log.entityType)}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-glass-text line-clamp-2">
                                {generateSummary(log)}
                              </p>
                              <div className="flex items-center gap-2 mt-1">
                                <span className="text-xs text-glass-text-3">
                                  {new Date(log.createdAt).toLocaleTimeString('en-US', {
                                    hour: '2-digit',
                                    minute: '2-digit',
                                  })}
                                </span>
                                <span className="text-xs text-glass-text-3">•</span>
                                <span className="text-xs text-glass-text-2">
                                  {log.actor?.fullName || log.actor?.email || 'System'}
                                </span>
                              </div>
                            </div>
                            <ChevronRight className="w-4 h-4 text-glass-text-3 flex-shrink-0" />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))
              )}
            </CardContent>
          </Card>
        </div>

        {/* Right Panel - Detail View */}
        <div className="col-span-12 lg:col-span-7 flex flex-col min-h-0">
          <Card className="flex-1 flex flex-col min-h-0">
            {selectedLog ? (
              <>
                <div className="p-6 border-b border-glass flex-shrink-0">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      {getActionBadge(selectedLog.action)}
                      <h2 className="text-xl font-semibold tracking-tightest text-glass-text mt-2">
                        {generateSummary(selectedLog)}
                      </h2>
                    </div>
                  </div>

                  <div className="flex items-center gap-4 text-sm text-glass-text-2">
                    <div className="flex items-center gap-1">
                      <User className="w-4 h-4" />
                      <span>{selectedLog.actor?.fullName || selectedLog.actor?.email || 'System'}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      <span>
                        {new Date(selectedLog.createdAt).toLocaleString('en-US', {
                          dateStyle: 'medium',
                          timeStyle: 'short',
                        })}
                      </span>
                    </div>
                  </div>
                </div>

                <CardContent className="flex-1 overflow-y-auto p-6 space-y-6">
                  {/* Contextual Data */}
                  {!developerMode && contextData && (
                    <div className="space-y-3">
                      <h3 className="font-semibold text-glass-text">Details</h3>
                      <div className="grid grid-cols-2 gap-4">
                        {contextData.jobTitle && (
                          <div>
                            <p className="text-xs text-glass-text-3 uppercase tracking-[0.05em]">Job</p>
                            <p className="text-sm font-medium text-glass-text">{contextData.jobTitle}</p>
                          </div>
                        )}
                        {contextData.kolName && (
                          <div>
                            <p className="text-xs text-glass-text-3 uppercase tracking-[0.05em]">KOL</p>
                            <p className="text-sm font-medium text-glass-text">{contextData.kolName}</p>
                          </div>
                        )}
                        {contextData.status && (
                          <div>
                            <p className="text-xs text-glass-text-3 uppercase tracking-[0.05em]">Status</p>
                            <p className="text-sm font-medium text-glass-text">{contextData.status}</p>
                          </div>
                        )}
                        {contextData.targetUser && (
                          <div>
                            <p className="text-xs text-glass-text-3 uppercase tracking-[0.05em]">Target User</p>
                            <p className="text-sm font-medium text-glass-text">{contextData.targetUser}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Developer Mode - System Metadata */}
                  {developerMode && (
                    <div className="space-y-3">
                      <h3 className="font-semibold text-glass-text flex items-center gap-2">
                        <Settings className="w-4 h-4" />
                        System Metadata
                      </h3>
                      <div className="rounded-xl border border-glass bg-glass-2 p-4 space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-glass-text-3">Entity Type:</span>
                          <span className="font-mono text-glass-text">{selectedLog.entityType}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-glass-text-3">Entity ID:</span>
                          <span className="font-mono text-xs text-glass-text">{selectedLog.entityId}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-glass-text-3">Actor ID:</span>
                          <span className="font-mono text-xs text-glass-text">{selectedLog.actorId || 'N/A'}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-glass-text-3">Log ID:</span>
                          <span className="font-mono text-xs text-glass-text">{selectedLog.id}</span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Changes */}
                  {selectedLog.after && (
                    <div className="space-y-3">
                      <h3 className="font-semibold text-glass-text">
                        {developerMode ? 'Raw JSON Data' : 'Data Snapshot'}
                      </h3>
                      {developerMode ? (
                        <div className="space-y-3">
                          {selectedLog.before && (
                            <details className="rounded-xl border border-rose-500/30 bg-rose-500/10 p-4">
                              <summary className="cursor-pointer text-sm font-medium text-rose-500 dark:text-rose-300 mb-2">
                                Before (Previous State)
                              </summary>
                              <pre className="text-xs text-glass-text-2 overflow-x-auto whitespace-pre-wrap">
                                {JSON.stringify(selectedLog.before, null, 2)}
                              </pre>
                            </details>
                          )}
                          <details className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-4" open>
                            <summary className="cursor-pointer text-sm font-medium text-emerald-500 dark:text-emerald-300 mb-2">
                              After (Current State)
                            </summary>
                            <pre className="text-xs text-glass-text-2 overflow-x-auto whitespace-pre-wrap">
                              {JSON.stringify(selectedLog.after, null, 2)}
                            </pre>
                          </details>
                        </div>
                      ) : (
                        <div className="rounded-xl border border-glass bg-glass-2 p-4">
                          <p className="text-sm text-glass-text-2">
                            {selectedLog.action === 'CREATE' && 'New entity created with the details shown above.'}
                            {selectedLog.action === 'UPDATE' && 'Entity was updated. Enable Developer Mode to see full changes.'}
                            {selectedLog.action === 'DELETE' && 'Entity was permanently deleted.'}
                            {!['CREATE', 'UPDATE', 'DELETE'].includes(selectedLog.action) && 
                              'Action performed successfully.'}
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </>
            ) : (
              <CardContent className="flex-1 flex items-center justify-center">
                <div className="text-center text-glass-text-3">
                  <Shield className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>Select an activity to view details</p>
                </div>
              </CardContent>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}
